"use client";

/**
 * On-demand call transcript — renders under the recording player in call
 * history and in the CallRecordingBar. The Transcribe button itself lives in
 * useCallTranscription (shared with InsuraMentor's "From my calls"); this file
 * is the reader: notes, seek, follow-along highlight.
 * It follows the app-wide recording (CallRecordingContext): clicking a line
 * plays this call from that second, and the line being spoken highlights.
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { draftCallNotes, callNotesToHtml, localAiAvailability, NOTEIQ_DISCLAIMER, type CallNotes, type DraftProgress } from "@/app/_module/services/localAI";
import { FileText, Loader2, AlertCircle } from "lucide-react";

import { Button } from "@/app/_module/components/ui/button";
import { useToast } from "@/app/_module/hooks/use-toast";
import { cn } from "@/app/_module/lib/utils";
import { useCallRecording, useCallRecordingClock, type CallRecording } from "@/app/_module/contexts/CallRecordingContext";
import { useCallTranscription, type Segment } from "../hooks/useCallTranscription";

const fmtTs = (s: number) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
};

export function CallTranscriptPanel({
  recording,
  className,
}: {
  /** Recording length (durationSecs) bounds transcription, not the call's length. */
  recording: CallRecording;
  className?: string;
}) {
  const { callId, recordingUrl, durationSecs: durationSeconds } = recording;
  const { toast } = useToast();
  const listRef = useRef<HTMLDivElement>(null);
  // Auto-follow the active line during playback; a manual scroll pauses the
  // follow for a few seconds so the agent can read elsewhere in long convos.
  const userScrollUntil = useRef(0);
  const player = useCallRecording();
  const { time } = useCallRecordingClock();
  const isCurrent = player?.current?.callId === callId;
  // On-device notes (Chrome Prompt API). null = not checked yet.
  const [aiReady, setAiReady] = useState<boolean | null>(null);
  const [notes, setNotes] = useState<CallNotes | null>(null);
  const [notesPhase, setNotesPhase] = useState<DraftProgress | null>(null);
  const [notesError, setNotesError] = useState<string | null>(null);
  const { transcript, clientPhase, requested, isStarting, startTranscription } = useCallTranscription({ callId, recordingUrl, durationSeconds });

  // Probe once. Cheap, and it decides whether the button renders at all —
  // showing "Draft notes" to the ~18% who cannot run it is worse than hiding it.
  useEffect(() => {
    let cancelled = false;
    localAiAvailability()
      .then((a) => !cancelled && setAiReady(a !== "unavailable"))
      .catch(() => !cancelled && setAiReady(false));
    return () => { cancelled = true; };
  }, []);

  const generateNotes = useCallback(async () => {
    const text = (transcript?.segments || []).map((s) => s.text).join(" ").trim();
    if (!text) return;
    setNotesError(null);
    setNotes(null);
    try {
      const result = await draftCallNotes(text, { onProgress: setNotesPhase });
      if (!result) {
        setNotesError("NoteIQ needs Chrome 138+ on a desktop computer.");
        return;
      }
      setNotes(result);
    } catch {
      setNotesError("Could not draft notes.");
    } finally {
      setNotesPhase(null);
    }
  }, [transcript?.segments]);

  const copyNotes = useCallback(async () => {
    if (!notes) return;
    try {
      await navigator.clipboard.writeText(callNotesToHtml(notes).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim());
      toast({ title: "Notes copied", description: "Paste into the lead note." });
    } catch {
      toast({ title: "Copy failed", variant: "destructive" });
    }
  }, [notes, toast]);

  const copyTranscript = useCallback(async () => {
    const text = (transcript?.segments || []).map((s) => `${fmtTs(s.start)}  ${s.text.trim()}`).join("\n");
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: "Transcript copied" });
    } catch {
      toast({ title: "Copy failed", variant: "destructive" });
    }
  }, [transcript?.segments, toast]);

  // The line being spoken, while this call is the one playing.
  const segments = transcript?.status === "completed" ? transcript.segments : [];
  const activeIdx = isCurrent ? segments.findIndex((s) => time >= s.start && time < (s.end || s.start + 5)) : -1;

  // Keep the active line in view while playing (unless the agent is reading
  // elsewhere — manual wheel/touch pauses the follow for 5s)
  useEffect(() => {
    if (activeIdx < 0) return;
    if (Date.now() < userScrollUntil.current) return;
    const el = listRef.current?.querySelector(`[data-seg="${activeIdx}"]`);
    (el as HTMLElement | null)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [activeIdx]);

  // Plays this call from that line, loading it first if another call (or none)
  // is playing. The player waits for metadata before seeking (WebKit).
  const seekTo = (seg: Segment) => {
    // Highlighting a line to copy it ends in a click on that line: don't jump the recording.
    const selection = window.getSelection();
    if (selection && !selection.isCollapsed && listRef.current?.contains(selection.anchorNode)) return;
    void player?.play(recording, seg.start);
  };

  // On-device run in flight → its own progress states
  if (clientPhase) {
    const pct =
      clientPhase.stage === "run" && clientPhase.total
        ? Math.min(99, Math.round(((clientPhase.processed || 0) / clientPhase.total) * 100))
        : clientPhase.stage === "model"
          ? clientPhase.pct || 0
          : 0;
    return (
      <div onClick={(e) => e.stopPropagation()} className={cn("mt-2 space-y-1.5", className)}>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
          {clientPhase.stage === "audio" && <span>Preparing the recording…</span>}
          {clientPhase.stage === "model" && (
            <span>
              Downloading speech model <span className="font-semibold text-gray-700">{clientPhase.pct || 0}%</span>
              <span className="text-gray-400"> · one-time, ~40MB</span>
            </span>
          )}
          {clientPhase.stage === "run" && (
            <span>
              Transcribing on this device{!(navigator as any).gpu ? " (CPU)" : ""}… <span className="font-semibold text-gray-700">{pct}%</span>
              <span className="text-gray-400">
                {" "}· {fmtTs(clientPhase.processed || 0)} / {fmtTs(clientPhase.total || 0)}
              </span>
            </span>
          )}
        </div>
        <div className="h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-gray-200">
          <div className="h-full rounded-full bg-primary transition-all duration-300" style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  }

  // Nothing yet and never pressed → just the button
  if (!transcript && !requested) {
    return (
      <div onClick={(e) => e.stopPropagation()} className={cn("mt-2", className)}>
        <Button
          size="sm"
          variant="outline"
          onClick={startTranscription}
          disabled={isStarting}
          className="h-7 gap-1.5 text-xs text-gray-600 hover:text-primary"
        >
          {isStarting ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileText className="h-3 w-3" />}
          Transcribe call
        </Button>
      </div>
    );
  }

  if (!transcript || transcript.status === "processing") {
    const total = transcript?.durationSeconds || 0;
    const done = transcript?.processedSeconds || 0;
    const pct = total > 0 ? Math.min(99, Math.round((done / total) * 100)) : 0;
    // ETA from the job's own measured pace so far
    let eta = "";
    if (transcript?.createdAt && total > 0 && done > 10) {
      const elapsed = (Date.now() - new Date(transcript.createdAt).getTime()) / 1000;
      const rate = done / Math.max(1, elapsed); // audio-seconds per wall-second
      const remaining = Math.max(0, (total - done) / Math.max(0.1, rate));
      eta = remaining > 90 ? `~${Math.ceil(remaining / 60)} min left` : `~${Math.ceil(remaining / 10) * 10}s left`;
    }
    return (
      <div onClick={(e) => e.stopPropagation()} className={cn("mt-2 space-y-1.5", className)}>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
          {total > 0 ? (
            <span>
              Transcribing… <span className="font-semibold text-gray-700">{pct}%</span>
              {eta && <span className="text-gray-400"> · {eta}</span>}
              <span className="text-gray-400"> · {fmtTs(done)} / {fmtTs(total)}</span>
            </span>
          ) : (
            <span>Preparing the recording…</span>
          )}
        </div>
        <div className="h-1.5 w-full max-w-xs overflow-hidden rounded-full bg-gray-200">
          <div className="h-full rounded-full bg-primary transition-all duration-700" style={{ width: `${pct}%` }} />
        </div>
      </div>
    );
  }

  if (transcript.status === "failed") {
    return (
      <div onClick={(e) => e.stopPropagation()} className={cn("mt-2 flex items-center gap-2 text-xs text-red-600", className)}>
        <AlertCircle className="h-3.5 w-3.5" />
        Transcription failed{transcript.error ? ` — ${transcript.error}` : ""}.
        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={startTranscription} disabled={isStarting}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    // Selectable on purpose: globals.css turns selection off inside buttons, and the panel often sits
    // in a clickable row; each line is a button too (click to seek).
    <div onClick={(e) => e.stopPropagation()} className={cn("mt-2 rounded-md border bg-white !select-text", className)}>
      <div className="flex items-center justify-between border-b px-3 py-1.5">
        <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
          <FileText className="h-3.5 w-3.5" /> Transcript
        </span>
        <div className="flex items-center gap-2">
          {transcript.segments.length > 0 && (
            <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={copyTranscript}>
              Copy
            </Button>
          )}
          {aiReady && transcript.segments.length > 0 && (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 px-2 text-xs"
              onClick={generateNotes}
              disabled={!!notesPhase}
            >
              {notesPhase ? "Drafting…" : notes ? "Redraft notes" : "Draft notes"}
            </Button>
          )}
          <span className="text-[10px] text-gray-400">click a line to jump the recording</span>
        </div>
      </div>

      {/* ── on-device notes ─────────────────────────────────────────────── */}
      {notesPhase && (
        <div className="border-b bg-violet-50/60 px-3 py-2 text-[11px] text-violet-800">
          {notesPhase.stage === "downloading"
            ? `Preparing the on-device model${notesPhase.pct != null ? ` — ${notesPhase.pct}%` : "…"}`
            : notesPhase.stage === "merging"
              ? "Merging summary…"
              : notesPhase.parts && notesPhase.parts > 1
                ? `Reading part ${notesPhase.part} of ${notesPhase.parts}…`
                : "Drafting notes on your device…"}
        </div>
      )}
      {notesError && !notesPhase && (
        <div className="border-b bg-red-50 px-3 py-2 text-[11px] text-red-700">{notesError}</div>
      )}
      {notes && !notesPhase && (
        <div className="border-b bg-violet-50/40 px-3 py-2 space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wide text-violet-700">
              Draft notes · generated on your device
            </span>
            <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" onClick={copyNotes}>
              Copy
            </Button>
          </div>
          <p className="text-xs leading-5 text-gray-800">{notes.summary}</p>
          {notes.nextStep && (
            <p className="text-xs text-gray-800"><strong>Next step:</strong> {notes.nextStep}</p>
          )}
          {notes.followUpAt && (
            <p className="text-xs text-gray-800"><strong>Follow up:</strong> {notes.followUpAt}</p>
          )}
          {notes.keyDetails.length > 0 && (
            <div className="text-xs text-gray-800">
              <strong>Details:</strong>
              <ul className="ml-4 list-disc">
                {notes.keyDetails.map((d, i) => (
                  <li key={i}>{d}</li>
                ))}
              </ul>
            </div>
          )}
          {notes.objections.length > 0 && (
            <p className="text-xs text-gray-800">
              <strong>Objections:</strong> {notes.objections.join("; ")}
            </p>
          )}
          <p className="text-[10px] text-gray-500">
            Outcome: {notes.outcome.replace(/_/g, " ")} · sentiment: {notes.sentiment}
            {" · "}<span className="italic">{NOTEIQ_DISCLAIMER}</span>
          </p>
        </div>
      )}
      {/* data-no-ptr: in the native shell this nested scroller sits inside
          PullToRefresh, whose touch tracking fights the pan — finger scrolling
          worked only intermittently on iOS. overscroll containment keeps a
          top-of-list swipe from chaining into the page/pull gesture. */}
      <div
        ref={listRef}
        data-no-ptr
        onWheel={() => { userScrollUntil.current = Date.now() + 5000; }}
        onTouchMove={() => { userScrollUntil.current = Date.now() + 5000; }}
        className="max-h-80 overflow-y-auto p-2 space-y-0.5"
        style={{ WebkitOverflowScrolling: "touch", overscrollBehavior: "contain" }}
      >
        {transcript.segments.length === 0 ? (
          <p className="p-2 text-xs text-muted-foreground">No speech detected in this recording.</p>
        ) : (
          transcript.segments.map((seg, i) => (
            <button
              key={i}
              data-seg={i}
              type="button"
              onClick={() => seekTo(seg)}
              className={`flex w-full items-start gap-2 rounded px-2 py-1 text-left text-xs transition-colors !select-text ${
                activeIdx === i ? "bg-primary/10 text-gray-900" : "hover:bg-gray-50 text-gray-700"
              }`}
            >
              <span className={`shrink-0 font-mono text-[10px] leading-4 ${activeIdx === i ? "text-primary font-bold" : "text-gray-400"}`}>
                {fmtTs(seg.start)}
              </span>
              <span className="leading-4">{seg.text}</span>
            </button>
          ))
        )}
      </div>
    </div>
  );
}
